import { apiFetch } from '../utils/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

export interface MarketWatchlist {
  market: 'US' | 'KR';
  tickers: string[];
  items?: { symbol: string; name?: string | null }[];
  count: number;
  source: string;
  updated_at: string | null;
}

export interface KrHoldingOwnership {
  symbol: string;
  name?: string | null;
  quantity: number;
  avg_entry_price?: number | null;
  current_price?: number | null;
  market_value?: number | null;
  unrealized_pl?: number | null;
  unrealized_pl_percent?: number | null;
  ownership_state: string;
  source: string;
  observed_at?: string | null;
}

export interface KrHoldingsOwnershipResponse {
  market: 'KR';
  broker: 'toss';
  items: KrHoldingOwnership[];
}

export async function fetchMarketWatchlist(market: 'US' | 'KR'): Promise<MarketWatchlist> {
  const response = await apiFetch(`${API_BASE_URL}/api/market-watchlists/${market}`);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`${market} watchlist 조회 실패 (${response.status}): ${text}`);
  }
  return response.json();
}

export async function fetchMarketWatchlists(): Promise<MarketWatchlist[]> {
  return Promise.all([fetchMarketWatchlist('US'), fetchMarketWatchlist('KR')]);
}

export async function fetchKrHoldingsOwnership(): Promise<KrHoldingsOwnershipResponse> {
  const response = await apiFetch(`${API_BASE_URL}/api/market-watchlists/KR/holdings-ownership`);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Toss KR 보유종목 ownership 조회 실패 (${response.status}): ${text}`);
  }
  return response.json();
}

export async function importTossKrHoldings(): Promise<MarketWatchlist> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/market-watchlists/KR/import-toss-holdings`,
    { method: 'POST' }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Toss KR 보유종목 import 실패 (${response.status}): ${text}`);
  }
  return response.json();
}

export async function importKrxSeedWatchlist(): Promise<MarketWatchlist> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/market-watchlists/KR/import-krx-seed`,
    { method: 'POST' }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`KRX seed import 실패 (${response.status}): ${text}`);
  }
  const data = await response.json();
  return data.watchlist || data;
}

export async function refreshGeminiKrWatchlist(): Promise<MarketWatchlist> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/market-watchlists/KR/refresh-gemini`,
    { method: 'POST' }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Gemini KR 후보 생성 실패 (${response.status}): ${text}`);
  }
  return response.json();
}

export async function refreshTossUsWatchlist(): Promise<MarketWatchlist> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/market-watchlists/US/refresh-toss-validation`,
    { method: 'POST' }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Toss US 후보 검증 실패 (${response.status}): ${text}`);
  }
  const data = await response.json();
  return data.watchlist || data;
}

export async function refreshUsLiveCandidates(): Promise<MarketWatchlist> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/market-watchlists/US/refresh-live-candidates`,
    { method: 'POST' }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`US LIVE 후보 생성/검증 실패 (${response.status}): ${text}`);
  }
  const data = await response.json();
  return data.watchlist || data;
}

export async function refreshTossKrWatchlist(): Promise<MarketWatchlist> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/market-watchlists/KR/refresh-toss-validation`,
    { method: 'POST' }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Toss KR 후보 검증 실패 (${response.status}): ${text}`);
  }
  const data = await response.json();
  return data.watchlist || data;
}
